import {
  useQuery,
  useMutation,
  queryCache,
  usePaginatedQuery,
  QueryResult,
  PaginatedQueryResult,
  QueryOptions,
} from 'react-query'
import {
  CreateProjectParams,
  Project,
  ProjectTableFilters,
  ProjectsResponse,
  CreateAreaParams,
  Area,
} from 'models/project'
import { ProjectRepository } from 'repository/project'

export const useFetchProjects = (filters: ProjectTableFilters) => {
  const {
    status,
    resolvedData,
    latestData = { totalItems: 0, projects: [] },
    error,
  }: PaginatedQueryResult<ProjectsResponse> = usePaginatedQuery(['projects', filters], () =>
    ProjectRepository.fetchAll(filters),
  )

  return {
    status,
    totalItems: resolvedData?.totalItems ?? latestData.totalItems,
    projects: resolvedData?.projects ?? latestData.projects,
    error,
  }
}

export const useFetchRecentProjects = () => {
  const { status, data, error }: QueryResult<ProjectsResponse> = useQuery(
    ['recent-projects'],
    ProjectRepository.getRecent,
  )

  return {
    status,
    projects: data && data.projects,
    error,
  }
}

export const useFetchProject = (projectId: string, config?: QueryOptions<Project>) => {
  const { status, data: project, error } = useQuery(
    !!projectId && ['project', { projectId }],
    () => ProjectRepository.fetchById(projectId),
    config,
  )
  return { status, project, error }
}

export const useCreateProject = () => {
  return useMutation((data: CreateProjectParams) => ProjectRepository.create(data))
}

export const useCreatePondera = () => {
  return useMutation((data: CreateProjectParams) => ProjectRepository.createPondera(data))
}

export const useUpdateProject = (projectId: string) => {
  return useMutation((data: Partial<Project>) => ProjectRepository.update(projectId, data), {
    onSuccess: (project: Project) => {
      queryCache.setQueryData(['project', { projectId: projectId.toString() }], project)
    },
  })
}

export const useDuplicateProject = () => {
  return useMutation((projectId: string) => ProjectRepository.duplicate(projectId))
}

export const useDeleteProject = () => {
  return useMutation((projectId: string) => ProjectRepository.delete(projectId), {
    onSuccess: () => {
      queryCache.refetchQueries(['projects'], { force: true })
    },
  })
}

export const useChangeArea = (projectId: string) => {
  return useMutation((data: CreateProjectParams) => ProjectRepository.changeArea(projectId, data), {
    onSuccess: (project: Project) => {
      queryCache.setQueryData(['project', { projectId }], project)
    },
  })
}

export const useFetchCompareAreas = (projectId: string) => {
  const { status, data: areas, error } = useQuery(
    !!projectId && ['compare-areas', { projectId }],
    () => ProjectRepository.fetchAreas(projectId),
  )
  return { status, areas, error }
}

export const useFetchArea = (projectId: string, areaId: string) => {
  const { status, areas, error } = useFetchCompareAreas(projectId)
  const compareArea = areas?.find((area) => {
    return area.id === parseInt(areaId)
  })

  return {
    status,
    area: compareArea,
    error,
  }
}

export const useDeleteArea = (projectId: string, areaId: string) => {
  ProjectRepository.deleteArea(projectId, areaId)
  //const { status, areas, error } = useFetchCompareAreas(projectId)
  return 'success'
}

export const useCreateCompareArea = (projectId: string) => {
  return useMutation((data: CreateAreaParams) => ProjectRepository.createArea(projectId, data), {
    onSuccess: (area: Area) => {
      queryCache.setQueryData(['compare-areas', { projectId }], (areas: Area[] = []) => [
        ...areas,
        area,
      ])
    },
  })
}

export const useGenerateProjectReport = (projectId: string, areaId?: string) => {
  return useMutation(() => ProjectRepository.generateReport(projectId, areaId))
}
